import { AxiosResponse } from 'axios';
import { RELEASED_DOCUMENT_TYPE } from 'front-office/pages/released-services/types/cash-check-document-type';
import { DateTime } from 'luxon';
import { ReleasedService } from 'shell/models/certificate';
import { HttpResponse } from 'shell/models/http-response';
import { CertificateForm } from '../form';

export const transformCertificate = (
  httpResponse: AxiosResponse<HttpResponse<ReleasedService>>
): CertificateForm => {
  const data = httpResponse.data.payload || {};

  let descriptionState = '';

  if (data.statoDocumento?.key?.codice === '1' || data.statoDocumento?.key?.codice === '2') {
    descriptionState = 'Rilasciato';
  }

  if (data.statoDocumento?.key?.codice === '3' || data.statoDocumento?.key?.codice === '4') {
    descriptionState = 'Annullato';
  }

  const isActVisibile =
    data?.tipoDocumento?.key?.tipoDocumento === RELEASED_DOCUMENT_TYPE.CARTA_DI_IDENTITA_IE ||
    data?.tipoDocumento?.key?.tipoDocumento === RELEASED_DOCUMENT_TYPE.CARTA_DI_IDENTITA_ID ||
    data?.tipoDocumento?.key?.tipoDocumento === RELEASED_DOCUMENT_TYPE.STATO_CIVILE;

  let act = '';
  if (
    data?.tipoDocumento?.key?.tipoDocumento === RELEASED_DOCUMENT_TYPE.CARTA_DI_IDENTITA_IE ||
    data?.tipoDocumento?.key?.tipoDocumento === RELEASED_DOCUMENT_TYPE.CARTA_DI_IDENTITA_ID
  ) {
    act = data?.numeroCartaIdentita || '';
  }

  if (data?.tipoDocumento?.key?.tipoDocumento === RELEASED_DOCUMENT_TYPE.STATO_CIVILE) {
    act = `ATTO ${data?.registroCertifStatoCivile?.annoAtto || ''} N. ${
      data?.registroCertifStatoCivile?.numeroAtto || ''
    } ${data?.registroCertifStatoCivile?.parteAtto || ''} ${
      data?.registroCertifStatoCivile?.serie1Atto || ''
    } ${data?.registroCertifStatoCivile?.serie2Atto || ''} R. ${
      data?.registroCertifStatoCivile?.registroAtto || ''
    }`;
  }

  if (data?.registroCertifStatoCivile?.idAnscAtto) {
    act = data?.registroCertifStatoCivile?.idAnscAtto;
  }

  const isApplicantIsAccountHolder =
    data?.codiceFiscaleRichiedente === data?.codiceFiscaleIntestatario;

  const isApplicantVisible =
    !!data?.codiceFiscaleRichiedente &&
    (!!data?.richiedente?.nome ||
      !!data?.richiedente?.cognome ||
      !!data?.richiedente?.dataNascita ||
      !!data?.richiedente?.descrizioneLuogoNascita) &&
    !isApplicantIsAccountHolder;

  const response: CertificateForm = {
    numberReceiptPos: !!data.numeroRicevutaPos,
    acronym: data.key.codiceSiglaRegistro || '',
    year: data.key.idAnnoDocumento || '',
    progressive: data.key.progressivoDocumento || '',
    dateForm: data.timestampRilascio ? DateTime.fromISO(data.timestampRilascio) : null,
    description: data.tipoDocumento?.descrizioneTipoDocumento || '',
    certificationNumber: data?.numeroCertificatoAnpr || '',
    documentType: data?.tipoDocumento?.key?.tipoDocumento || '',
    holderFiscalCode: data.codiceFiscaleIntestatario || '',
    applicantFiscalCode: data.codiceFiscaleRichiedente || '',
    releaseMode: data.modalitaRilascio
      ? {
          id: data.modalitaRilascio?.key?.codiceModalitaRilascio || '',
          value: data.modalitaRilascio?.descrizione || '',
        }
      : null,
    holderDateOfBirth: data.intestatario?.dataNascita
      ? DateTime.fromISO(data.intestatario.dataNascita)
      : undefined,
    holderName: `${data.intestatario?.cognome || ''} ${data.intestatario?.nome || ''}`,
    holderPlaceOfBirth: data.intestatario?.descrizioneLuogoNascita,
    applicantDateOfBirth: data.richiedente?.dataNascita
      ? DateTime.fromISO(data.richiedente.dataNascita)
      : undefined,
    applicantName: `${data.richiedente?.cognome || ''} ${data.richiedente?.nome || ''}`,
    applicantPlaceOfBirth: data.richiedente?.descrizioneLuogoNascita,
    payment: data.tipoPagamento || '',
    state: descriptionState || data.statoDocumento?.descrizione || '',
    act: isActVisibile ? act : '',
    numberReceivedPos: data?.numeroRicevutaPos,
    stamp: data.importoBollo || '0.00',
    secretary: data.importoDirittiSegreteria || '0.00',
    civilStatus: data.importoDirittiStatoCivile || '0.00',
    fixed: data.importoDirittiFissi || '0.00',
    refund: data.importoSpese || '0.00',
    emitted: data.codiceOperatoreRilascio || '',
    service: data.codiceServizioRilascio || '',
    date: data.timestampRilascio ? DateTime.fromISO(data.timestampRilascio) : null,
    time: data.timestampRilascio ? DateTime.fromISO(data.timestampRilascio) : null,
    justification: data.registroDocAnnullatiVO?.tipoGiustificativo || 'E',
    void: data.codiceStato === '3' || data.codiceStato === '4' ? true : false,
    dateVoid: data.registroDocAnnullatiVO?.timestampAnnullamento
      ? DateTime.fromISO(data.registroDocAnnullatiVO?.timestampAnnullamento)
      : null,
    timeVoid: data.registroDocAnnullatiVO?.timestampAnnullamento
      ? DateTime.fromISO(data.registroDocAnnullatiVO?.timestampAnnullamento)
      : null,
    voidBy: data.registroDocAnnullatiVO?.nomeOperatoreAnnullamento || '',
    birthDate: data.timestampRilascio ? DateTime.fromISO(data.timestampRilascio) : null,
    certifications: (data.tipoCertificatiContestuali || []).map((certification) => ({
      key: certification.key?.codiceDocumento || '',
      label: certification.descrizioneTipoDocumento || '',
    })),
    documentCode: data?.tipoDocumento?.key?.codiceDocumento || '',
    applicantReleaseReason: data?.richiedente?.motivoRilascio || '',
    serviceVoidReason: '',
    disabledPaymentList: false,
    isApplicantVisible,
    isApplicantIsAccountHolder,
  };

  return response;
};
